import { ApolloClient, ApolloQueryResult } from '@apollo/client';
import axios from 'axios';
import JSZip from 'jszip';
import _, { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { IMediaCollection } from 'types/MediaCollection';
import { IMediaEntry } from 'types/MediaEntry';
import IMediaEntryCollection from 'types/MediaEntryCollection';
import IProject from 'types/Project';
import { IFile, IMAGE_ZIP_FILE_NAME, downloadFile, downloadZipFile } from 'utils/fileUtils';
import {
  IMediaCollectionQueryResults,
  IMediaEntryQueryResults,
  mediaCollectionQuery,
  mediaEntryQuery,
} from '../photos-queries';

// Get all the media entries from an array of media collections
function getMediaEntriesFromCollections(mediaCollections: IMediaCollection[]) {
  return _.flatMap(mediaCollections, (collection) => {
    return collection.mediaEntries.filter((entry) => !entry.deletedOn);
  });
}

function getCollectionForMediaFromCollections(mediaCollections: IMediaCollection[], mediaEntry: IMediaEntry) {
  return _.find(mediaCollections, (collection) => {
    return collection.id === mediaEntry.mediaCollectionId;
  });
}

function downloadCollection(mediaCollection: IMediaCollection) {
  const zipper = new JSZip();
  const fileName = IMAGE_ZIP_FILE_NAME;
  const wrapper = zipper.folder(fileName);
  if (!wrapper) {
    return;
  }
  return PhotoUtilities.downloadCollectionBlob(mediaCollection).then((files) => {
    PhotoUtilities.updateSameFileNames(files);
    PhotoUtilities.addMediaFilesToZipper(wrapper, files);
    return downloadZipFile(zipper, IMAGE_ZIP_FILE_NAME);
  });
}

function downloadCollections(mediaCollections: IMediaCollection[]) {
  const zipper = new JSZip();
  const wrapper = zipper.folder(IMAGE_ZIP_FILE_NAME);
  if (!wrapper) {
    return;
  }
  const promisedCollections = _.map(mediaCollections, (collection) => {
    return PhotoUtilities.downloadCollectionBlob(collection);
  });

  return Promise.all(promisedCollections).then((entryFiles) => {
    const flatEntries = _.flatten(entryFiles);

    PhotoUtilities.updateSameFileNames(flatEntries);
    PhotoUtilities.addMediaFilesToZipper(wrapper, flatEntries);

    return downloadZipFile(zipper, IMAGE_ZIP_FILE_NAME);
  });
}

function downloadCollectionBlob(mediaCollection: IMediaCollection) {
  const promises = _.map(mediaCollection.mediaEntries, (mediaEntry) => {
    return PhotoUtilities.downloadMediaEntryBlob(mediaEntry).then((blob) =>
      PhotoUtilities.convertToFile({ mediaEntry, encompassingCollection: mediaCollection }, blob)
    );
  });

  return Promise.all(promises);
}

function downloadMediaEntry(entryCollection: IMediaEntryCollection) {
  return PhotoUtilities.downloadMediaEntryBlob(entryCollection.mediaEntry).then((blob) => {
    const file = PhotoUtilities.convertToFile(entryCollection, blob);
    return downloadFile({ ...file, fileName: `${file.fileName}.jpg` });
  });
}

function downloadMediaEntries(entryCollections: IMediaEntryCollection[]) {
  const zipper = new JSZip();
  const wrapper = zipper.folder(IMAGE_ZIP_FILE_NAME);
  if (!wrapper) {
    return;
  }
  const promises = _.map(entryCollections, (entryCollection) => {
    return PhotoUtilities.downloadMediaEntryBlob(entryCollection.mediaEntry).then((blob) =>
      PhotoUtilities.convertToFile(entryCollection, blob)
    );
  });

  return Promise.all(promises).then((files) => {
    PhotoUtilities.updateSameFileNames(files);
    PhotoUtilities.addMediaFilesToZipper(wrapper, files);
    return downloadZipFile(zipper, IMAGE_ZIP_FILE_NAME);
  });
}

function downloadMediaEntryBlob(entry: IMediaEntry): Promise<Blob> {
  const imageurl = entry.fileUrl;
  return axios({
    method: 'GET',
    responseType: 'blob', // important
    url: imageurl ? imageurl : '',
  }).then((response) => response.data);
}

function convertToFile(entryCollection: IMediaEntryCollection, file: Blob): IFile {
  const fileName = PhotoUtilities.getMediaEntryImageName(
    entryCollection.mediaEntry,
    entryCollection.encompassingCollection.project?.rootProject
  );
  return { file, fileName };
}

function addMediaFilesToZipper(zipper: JSZip, files: IFile[]) {
  _.map(files, ({ file, fileName }) => zipper.file(`${fileName}.jpg`, file, { base64: true }));
}

function getMediaEntryImageName(entry: IMediaEntry, rootProject?: IProject | null) {
  // Todo check that timezone is correct
  const date = DateTime.fromISO(entry.captureDate!, { zone: 'utc' });

  // Feb-22-2019
  const localeDateString = date.toLocaleString(DateTime.DATE_MED).replace(/(, | )/g, '-');

  if (rootProject) {
    return `${rootProject.title}-${localeDateString}`;
  } else {
    return localeDateString;
  }
}

function updateSameFileNames(files: IFile[]) {
  const groupedTitles = _.groupBy(files, 'fileName');

  _.forIn(groupedTitles, (matches, fileName) => {
    if (matches.length && matches.length > 1) {
      matches.forEach((match, index) => {
        match.fileName = `${fileName}-${index + 1}`;
      });
    }
  });
}

// temp types for loaders to make declarations less confusing, `IMediaEntry['id'][][]`
type TMediaEntryId = IMediaEntry['id'];
type TMediaCollectionId = IMediaCollection['id'];

const QUERY_BATCH_SIZE = 50; // use the busybusy grapqhl default

export async function loadMediaEntry(
  apolloClient: ApolloClient<any>,
  mediaEntryId: TMediaEntryId | TMediaEntryId[]
): Promise<IMediaEntry[]> {
  const containsIds: TMediaEntryId[] = _.castArray<TMediaEntryId>(mediaEntryId); // the filter argument must be an array
  const batches: TMediaEntryId[][] = _.chunk(containsIds, QUERY_BATCH_SIZE);

  // Note: Apollo Client combines up to 10 queries into a single request, so this doesn't generate as many network calls as might be expected
  return Promise.all(
    batches.map((batchIds) =>
      apolloClient
        .query({
          fetchPolicy: 'network-only', // no caching
          query: mediaEntryQuery,
          variables: {
            first: QUERY_BATCH_SIZE,
            filter: {
              id: { contains: batchIds },
              deletedOn: { isNull: true },
            },
          },
        })
        // reduce results to the relevant bits
        .then((queryResult: ApolloQueryResult<IMediaEntryQueryResults>) => queryResult.data.mediaEntries)
    )
    // return the flattened the array of arrays
  ).then(_.flatten);
}

export async function loadMediaCollection(
  apolloClient: ApolloClient<any>,
  mediaCollectionId: TMediaCollectionId | TMediaCollectionId[]
): Promise<IMediaCollection[]> {
  const containsIds: TMediaCollectionId[] = _.castArray<TMediaCollectionId>(mediaCollectionId); // the filter argument must be an array
  const batches: TMediaCollectionId[][] = _.chunk(containsIds, QUERY_BATCH_SIZE);

  return Promise.all(
    batches.map((batch) =>
      apolloClient
        .query({
          fetchPolicy: 'network-only', // no caching
          query: mediaCollectionQuery,
          variables: {
            first: QUERY_BATCH_SIZE,
            filter: {
              id: { contains: batch },
              deletedOn: { isNull: true },
            },
            sort: [{ createdOn: 'desc' }],
          },
        })
        // reduce results to the relevant bits
        .then((apolloResult: ApolloQueryResult<IMediaCollectionQueryResults>) => {
          return apolloResult.data.mediaCollections.map((collection) => {
            const newCollection = cloneDeep(collection);
            // mutate the mediaEntries collection, removing all deleted mediaEntries and sorting the remainder
            newCollection.mediaEntries = _.reject(newCollection.mediaEntries, 'deletedOn');
            newCollection.mediaEntries = _.sortBy(newCollection.mediaEntries, ['createdOn', 'captureDate']);

            return newCollection;
          });
        })
    )
    // return the flattened the array of arrays
  ).then(_.flatten);
}

export function getResizedPhotoUrl(fileUrl: string, size: number) {
  return `${fileUrl}?w=${size}&h=${size}`;
}

export function getPhotoThumbnailUrl(fileUrl: string) {
  return getResizedPhotoUrl(fileUrl, 280);
}

const PhotoUtilities = {
  addMediaFilesToZipper,
  convertToFile,
  downloadCollection,
  downloadCollectionBlob,
  downloadCollections,
  downloadMediaEntries,
  downloadMediaEntry,
  downloadMediaEntryBlob,
  getCollectionForMediaFromCollections,
  getMediaEntriesFromCollections,
  getMediaEntryImageName,
  updateSameFileNames,
  loadMediaEntry,
  loadMediaCollection,
};

export default PhotoUtilities;
